import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Pagination,
  Select,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  CASH_RECEIPT_WRITE_ROLES,
  fetchAllCashReceipts,
  isCancellable,
  isIssuable,
  useCashReceiptMutations,
  useCashReceiptsQuery,
  type CashReceiptRow,
  type CashReceiptSearch,
  type CashReceiptSort,
} from '../api/cashReceiptApi';
import { defaultCashReceiptSearch, downloadCsv, formatDateTime, todayKst, toCsv, useCashReceiptLabels, won } from '../lib/cashReceiptLib';
import { CashReceiptSearchForm } from './CashReceiptSearchForm';

/*
 * 현금영수증 발급/조회(고도몰 같은 이름 화면) — 신청·대기·완료 처리된 주문(과 개별발급분) 목록.
 * 선택 일괄발급(실패·취소 건 재발급), 행별 취소, 영수증 보기, 엑셀(CSV). 상세 정보는 주문 상세의 현금영수증 카드.
 */

const PAGE_SIZES = [10, 20, 50, 100] as const;
const SORTS: ReadonlyArray<{ value: CashReceiptSort; label: string }> = [
  { value: 'REQUESTED_DESC', label: '신청일 ↓' },
  { value: 'REQUESTED_ASC', label: '신청일 ↑' },
  { value: 'ORDER_NO_DESC', label: '주문번호 ↓' },
  { value: 'ORDER_NO_ASC', label: '주문번호 ↑' },
];

export const CashReceiptListTab = () => {
  const L = useCashReceiptLabels();
  const { t } = L;
  const canWrite = useHasRole(CASH_RECEIPT_WRITE_ROLES);
  const [search, setSearch] = useState<CashReceiptSearch>(defaultCashReceiptSearch);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(10);
  const [sort, setSort] = useState<CashReceiptSort>('REQUESTED_DESC');
  const query = useCashReceiptsQuery(search, page, size, sort);
  const { bulkIssue, cancel } = useCashReceiptMutations();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [cancelTarget, setCancelTarget] = useState<CashReceiptRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [exporting, setExporting] = useState(false);

  const status = query.error?.status;
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const issuableCount = selectedRows.filter(isIssuable).length;
  const resetSelection = () => setSelected(new Set());
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const doBulkIssue = () =>
    bulkIssue.mutate(selectedRows, {
      onSuccess: (r) => {
        const msg = t('orders.cashReceiptAdmin.toast.bulkIssued', {
          defaultValue: '발급 요청 {{issued}}건 · 제외 {{skipped}}건 · 실패 {{failed}}건',
          issued: r.issued,
          skipped: r.skipped,
          failed: r.failed.length,
        });
        if (r.failed.length > 0) toast.error(`${msg}\n${r.failed.map((f) => `${f.orderNo}: ${f.reason}`).join('\n')}`);
        else toast.success(msg);
        resetSelection();
      },
    });

  const doCancel = () => {
    if (!cancelTarget) return;
    cancel.mutate(
      { row: cancelTarget, reason: cancelReason.trim() },
      {
        onSuccess: () => {
          toast.success(t('orders.cashReceipt.toast.canceled', '현금영수증을 취소했습니다.'));
          setCancelTarget(null);
          setCancelReason('');
        },
        onError: (e) => toast.error(friendlyMessage(e, t('orders.cashReceipt.toast.cancelFail', '현금영수증 취소에 실패했습니다.'))),
      },
    );
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchAllCashReceipts(search, sort);
      const header = [
        '번호', '신청일자', '처리일자', '주문번호', '신청자', '과세/면세', '발급금액', '공급액', '부가세', '면세',
        '현재 결제금액', '결제방법', '주문상태', '발행상태', '발급구분', '발행용도', '인증 종류', '인증번호', '거래번호', '실패 사유',
      ];
      const body = all.map((r, i) => [
        all.length - i, formatDateTime(r.requestedAt), r.processedAt ? formatDateTime(r.processedAt) : '', r.orderNo ?? '',
        r.requesterName, L.taxType(r.taxType), r.amount, r.supplyAmount, r.vatAmount, r.taxFreeAmount, r.orderAmount ?? '',
        r.paymentMethod ? L.paymentMethod(r.paymentMethod) : '', r.orderStatus ? L.orderStatus(r.orderStatus) : '',
        L.issueStatus(r.issueStatus), L.receiptSource(r.receiptSource), L.receiptType(r.receiptType), L.identityType(r.identityType),
        r.identityMasked ?? '', r.issueNumber ?? '', r.failureMessage ?? '',
      ]);
      downloadCsv(`현금영수증_${todayKst()}.csv`, toCsv(header, body));
    } catch (e) {
      toast.error(friendlyMessage(e, t('orders.cashReceiptAdmin.toast.exportFail', '엑셀을 만들지 못했습니다.')));
    } finally {
      setExporting(false);
    }
  };

  // BE 목록 API 배포 전 — 오류 대신 안내만.
  if (status === 404) {
    return (
      <div className="rounded-lg p-6 text-sm" style={{ border: '1px solid var(--divider-strong)', color: 'var(--content-muted)' }} data-testid="cash-receipt-not-deployed">
        {t('orders.cashReceiptAdmin.notDeployed', '서버 반영 후 사용할 수 있습니다. 그 전에는 주문 상세의 현금영수증 카드에서 주문별로 조회·발급·취소할 수 있습니다.')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="cash-receipt-list">
      <CashReceiptSearchForm
        initial={search}
        onSearch={(q) => {
          setSearch(q);
          setPage(1);
          resetSelection();
        }}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm" style={{ color: 'var(--content-default)' }}>
          {t('orders.cashReceiptAdmin.total', { defaultValue: '검색 {{total}}건', total: total.toLocaleString('ko-KR') })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-32" value={sort} onChange={(e) => { setSort(e.target.value as CashReceiptSort); setPage(1); }} aria-label={t('orders.cashReceiptAdmin.sort', '정렬')}>
            {SORTS.map((o) => (
              <option key={o.value} value={o.value}>{t(`orders.cashReceiptAdmin.sortOption.${o.value}`, o.label)}</option>
            ))}
          </Select>
          <Select className="w-28" value={String(size)} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }} aria-label={t('orders.cashReceiptAdmin.pageSize', '표시 개수')}>
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{t('orders.cashReceiptAdmin.pageSizeOption', { defaultValue: '{{n}}개 보기', n })}</option>
            ))}
          </Select>
        </div>
      </div>

      <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
        {t('orders.cashReceiptAdmin.pgNotice', '결제창(Toss)에서 자동 발급된 현금영수증은 이미 발급된 상태이므로, Toss 상점관리자에서 확인한 뒤 발급하세요.')}
      </p>

      {query.isError ? (
        <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
          {friendlyMessage(query.error, t('orders.cashReceiptAdmin.loadError', '목록을 불러오지 못했습니다.'))}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--divider-strong)' }}>
          <table className="w-full min-w-[1200px] text-[13px]" data-testid="cash-receipt-table">
            <thead style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
              <tr>
                <th className="w-10 px-2 py-2">
                  <Checkbox checked={allOnPage} onCheckedChange={(v) => setSelected(v === true ? new Set(rows.map((r) => r.id)) : new Set())} aria-label={t('orders.cashReceiptAdmin.selectAll', '전체 선택')} />
                </th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.no', '번호')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.requestedAt', '신청일자')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.processedAt', '처리일자')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.orderNo', '주문번호')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.requester', '신청자')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.taxType', '과세/면세')}</th>
                <th className="px-2 py-2 text-right">{t('orders.cashReceiptAdmin.col.amount', '발급금액')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.paymentMethod', '결제방법')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.orderStatus', '주문상태')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.issueStatus', '발행상태')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.info', '정보')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.receipt', '영수증')}</th>
                <th className="px-2 py-2 text-left">{t('orders.cashReceiptAdmin.col.action', '처리')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-2 py-10 text-center" style={{ color: 'var(--content-muted)' }}>
                    {query.isPending ? t('orders.cashReceiptAdmin.loading', '불러오는 중…') : t('orders.cashReceiptAdmin.empty', '검색된 내역이 없습니다.')}
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.id} className="border-t align-top" style={{ borderColor: 'var(--divider-soft)' }} data-testid="cash-receipt-row">
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={(v) => toggle(r.id, v === true)} aria-label={r.orderNo ?? r.requesterName} />
                    </td>
                    <td className="px-2 py-2 tabular-nums">{total - ((page - 1) * size + i)}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{formatDateTime(r.requestedAt)}</td>
                    <td className="whitespace-nowrap px-2 py-2 tabular-nums">{r.processedAt ? formatDateTime(r.processedAt) : '-'}</td>
                    <td className="px-2 py-2 tabular-nums">
                      {r.orderId && r.orderNo ? (
                        <Link to={`/orders/${r.orderId}`} className="text-[color:var(--accent-default)] underline">{r.orderNo}</Link>
                      ) : (
                        <span style={{ color: 'var(--content-muted)' }}>{t('orders.cashReceiptAdmin.manual', '개별발급')}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">{r.requesterName || '-'}</td>
                    <td className="whitespace-nowrap px-2 py-2">{L.taxType(r.taxType)}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                      {r.amountChanged ? (
                        <span
                          style={{ color: 'var(--status-danger-fg)', fontWeight: 600 }}
                          title={t('orders.cashReceiptAdmin.amountChangedHint', { defaultValue: '신청 후 결제금액이 바뀌었습니다(현재 {{amount}}). 재발급이 필요한지 확인하세요.', amount: won(r.orderAmount ?? 0) })}
                          data-testid="cash-receipt-amount-changed-cell"
                        >
                          {won(r.amount)}
                          <div className="text-[11px] font-normal">→ {won(r.orderAmount ?? 0)}</div>
                        </span>
                      ) : (
                        won(r.amount)
                      )}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{L.paymentMethod(r.paymentMethod)}</td>
                    <td className="whitespace-nowrap px-2 py-2">{L.orderStatus(r.orderStatus)}</td>
                    <td className="whitespace-nowrap px-2 py-2" title={r.failureMessage ?? undefined}>
                      <div style={r.issueStatus === 'FAILED' ? { color: 'var(--status-danger-fg)' } : r.issueStatus === 'ISSUED' ? { color: 'var(--accent-default)' } : undefined}>
                        {L.issueStatus(r.issueStatus)}
                      </div>
                      {r.receiptSource ? <div className="text-[11px]" style={{ color: 'var(--content-muted)' }}>({L.receiptSource(r.receiptSource)})</div> : null}
                    </td>
                    <td className="px-2 py-2">
                      {r.orderId ? (
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/orders/${r.orderId}`}>{t('orders.cashReceiptAdmin.view', '보기')}</Link>
                        </Button>
                      ) : (
                        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{r.goodsName ?? '-'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {r.receiptUrl ? (
                        <Button asChild size="sm" variant="outline">
                          <a href={r.receiptUrl} target="_blank" rel="noopener noreferrer" data-testid="cash-receipt-receipt-link">{t('orders.cashReceiptAdmin.receipt', '영수증')}</a>
                        </Button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {canWrite && isCancellable(r) ? (
                        <Button size="sm" variant="danger" onClick={() => setCancelTarget(r)} data-testid="cash-receipt-row-cancel">
                          {t('orders.cashReceiptAdmin.cancel', '취소')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {canWrite ? (
          <Button size="sm" variant="outline" disabled={issuableCount === 0 || bulkIssue.isPending} onClick={doBulkIssue} data-testid="cash-receipt-bulk-issue">
            {bulkIssue.isPending
              ? t('orders.cashReceiptAdmin.issuing', '발급 요청 중…')
              : t('orders.cashReceiptAdmin.bulkIssue', { defaultValue: '선택한 현금영수증 일괄발급 ({{count}})', count: issuableCount })}
          </Button>
        ) : (
          <span />
        )}
        <Button size="sm" variant="outline" onClick={() => void exportCsv()} disabled={exporting || total === 0} data-testid="cash-receipt-excel">
          {exporting ? t('orders.cashReceiptAdmin.exporting', '만드는 중…') : t('orders.cashReceiptAdmin.excel', '엑셀 다운로드')}
        </Button>
      </div>
      <Pagination total={total} currentPage={page} pageSize={size} onPageChange={(p) => { setPage(p); resetSelection(); }} showGoTo={false} />

      <CashReceiptGuide />

      <Dialog open={cancelTarget !== null} onOpenChange={(o) => { if (!o) setCancelTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.cashReceiptAdmin.cancelTitle', '현금영수증 취소')}</DialogTitle>
            <DialogDescription>
              {t('orders.cashReceiptAdmin.cancelDescription', {
                defaultValue: '{{target}} 현금영수증 잔액 {{amount}}을 모두 취소합니다. 부분 취소는 주문 상세에서 할 수 있습니다.',
                target: cancelTarget?.orderNo ?? cancelTarget?.requesterName ?? '',
                amount: won(cancelTarget?.remainingAmount ?? 0),
              })}
            </DialogDescription>
          </DialogHeader>
          <Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} maxLength={200} placeholder={t('orders.cashReceiptAdmin.cancelReason', '취소 사유(선택)')} data-testid="cash-receipt-cancel-reason" />
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setCancelTarget(null)}>{t('orders.cashReceiptAdmin.close', '닫기')}</Button>
            <Button size="sm" variant="danger" disabled={cancel.isPending} onClick={doCancel} data-testid="cash-receipt-cancel-confirm">
              {t('orders.cashReceiptAdmin.cancel', '취소')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** 고도몰 하단 안내 — 의무발행·상태별 처리. */
const CashReceiptGuide = () => {
  const { t } = useCashReceiptLabels();
  const lines = [
    t('orders.cashReceiptAdmin.guide.mandatory', '현금영수증 의무발행 업종은 거래 건당 10만원 이상 현금 결제(무통장입금 등) 시 소비자가 요청하지 않아도 국세청 지정번호(010-000-1234)로 발급해야 합니다. 미발급 시 미발급액의 20% 가산세가 부과됩니다.'),
    t('orders.cashReceiptAdmin.guide.auto', '무통장입금 주문은 입금확인 시 자동 발급되고, 주문 전체 취소·환불 시 자동 취소됩니다.'),
    t('orders.cashReceiptAdmin.guide.issue', '일괄발급은 발행상태가 "실패"·"취소"인 주문을 저장된 신청 정보로 다시 발급합니다. 신청 정보를 바꾸려면 주문 상세에서 발급하세요.'),
    t('orders.cashReceiptAdmin.guide.cancel', '취소는 쇼핑몰·개별발급으로 발급 완료된 건만 할 수 있습니다. 고도몰 이관분과 결제창(Toss) 발급분은 Toss 상점관리자에서 처리하세요.'),
    t('orders.cashReceiptAdmin.guide.changed', '신청 후 결제금액이 바뀐 건은 발급금액이 빨간색으로 표시됩니다. 운영 방침에 맞게 취소 후 재발급하세요.'),
  ];
  return (
    <div className="rounded-lg p-4 text-[12px] leading-relaxed" style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }} data-testid="cash-receipt-guide">
      <p className="mb-1 font-semibold" style={{ color: 'var(--content-default)' }}>{t('orders.cashReceiptAdmin.guide.title', '안내')}</p>
      <ul className="list-disc space-y-0.5 pl-4">
        {lines.map((l) => <li key={l}>{l}</li>)}
      </ul>
    </div>
  );
};
